/**
 * People Screen
 *
 * Browse organization members using cached data from useOrgUsersQuery.
 * Filters locally by username/display_name — no separate search API call.
 * Click a user to view their profile.
 */

import { useState } from 'react';
import { useSessionState } from '@/hooks/useSessionState';
import { useLocalFilter } from '@/hooks/useLocalFilter';
import { SessionKey } from '@/types/storage';
import { useActiveOrganization } from '@/hooks/useActiveOrganization';
import { useOrgUsersQuery } from '@/hooks/queries';
import { useThemeColors } from '@/styles/colors';
import { spacing, radius } from '@/styles/design-tokens';
import { Avatar } from '@/components/ui/avatar';
import UserProfile from '../UserProfile';
import { ShareButton } from '@/components/core/ShareButton/ShareButton';
import { SearchInput } from '@/components/core';
import { DeepLinkEntity } from '@/utils/deep-linking';

const USER_SEARCH_KEYS: Array<'username' | 'display_name'> = [
  'username',
  'display_name',
];

interface PeopleProps {
  selectedUserId?: string | null;
  onSelectedUserIdChange?: (userId: string | null) => void;
  onBoardClick?: (boardId: string) => void;
  onProfileLoad?: (displayName: string) => void;
}

export default function People({
  selectedUserId: controlledUserId,
  onSelectedUserIdChange,
  onBoardClick,
  onProfileLoad,
}: PeopleProps = {}) {
  const { activeOrganizationId } = useActiveOrganization();
  const colors = useThemeColors();

  const [query, setQuery] = useSessionState<string>(
    SessionKey.LB_PEOPLE_QUERY,
    ''
  );
  const [internalUserId, setInternalUserId] = useState<string | null>(null);

  // Support both controlled and uncontrolled modes
  const selectedUserId =
    controlledUserId !== undefined ? controlledUserId : internalUserId;
  const setSelectedUserId = (id: string | null) => {
    onSelectedUserIdChange ? onSelectedUserIdChange(id) : setInternalUserId(id);
  };

  const { data: orgUsersData, isLoading } =
    useOrgUsersQuery(activeOrganizationId);

  // Local filtering by username or display_name — data already in frontend state
  const allUsers = orgUsersData?.users ?? [];
  const matchedUsers = useLocalFilter(allUsers, query, USER_SEARCH_KEYS);
  const filteredUsers = [...matchedUsers].sort((a, b) =>
    a.display_name.localeCompare(b.display_name)
  );

  if (selectedUserId) {
    return (
      <div style={{ padding: spacing.lg }}>
        <UserProfile
          userId={selectedUserId}
          onBoardClick={onBoardClick}
          onProfileLoad={onProfileLoad}
        />
      </div>
    );
  }

  return (
    <div style={{ padding: spacing.lg }}>
      {/* Search input */}
      <SearchInput
        value={query}
        onChange={setQuery}
        placeholder="Search by name or username..."
        style={{ maxWidth: '400px', marginBottom: spacing.lg }}
      />

      {/* Loading state */}
      {isLoading && (
        <p style={{ color: colors.text.secondary, fontSize: '0.875rem' }}>
          Loading users...
        </p>
      )}

      {/* User grid */}
      {!isLoading && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
            gap: spacing.md,
          }}
        >
          {filteredUsers.map(user => (
            <div
              key={user.id}
              role="button"
              tabIndex={0}
              onClick={() => setSelectedUserId(user.id)}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setSelectedUserId(user.id);
                }
              }}
              style={{
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '0.5rem',
                padding: spacing.md,
                borderRadius: '0.5rem',
                border: `1px solid ${colors.border.default}`,
                backgroundColor: colors.offWhite,
                cursor: 'pointer',
                transition: 'box-shadow 0.15s',
                textAlign: 'center',
              }}
            >
              {/* Copy link */}
              <div
                style={{
                  position: 'absolute',
                  top: '0.25rem',
                  right: '0.25rem',
                }}
              >
                <ShareButton
                  entity={DeepLinkEntity.USER}
                  id={user.id}
                  ariaLabel={`Copy link to ${user.display_name}'s profile`}
                  style={{ height: '1.75rem', width: '1.75rem' }}
                />
              </div>

              {/* Avatar */}
              <Avatar
                src={user.profile_image}
                displayName={user.display_name}
                userId={user.id}
                size={48}
              />

              {/* Name */}
              <span
                style={{
                  fontWeight: 500,
                  fontSize: '0.875rem',
                  color: colors.text.primary,
                }}
              >
                {user.display_name}
              </span>

              {/* Username */}
              <span
                style={{ fontSize: '0.75rem', color: colors.text.secondary }}
              >
                @{user.username}
              </span>

              {/* Role badge */}
              <span
                style={{
                  fontSize: '0.625rem',
                  fontWeight: 600,
                  padding: '0.125rem 0.5rem',
                  borderRadius: radius.full,
                  backgroundColor: colors.primary,
                  color: colors.text.inverse,
                  textTransform: 'uppercase',
                  letterSpacing: '0.025em',
                }}
              >
                {user.role}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Empty state */}
      {!isLoading && filteredUsers.length === 0 && (
        <p style={{ color: colors.text.secondary, fontSize: '0.875rem' }}>
          {query ? 'No users found matching your search.' : 'No users found.'}
        </p>
      )}
    </div>
  );
}
